import { SafeHtml } from "@angular/platform-browser";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input } from "@angular/core";
import { NgClass } from "@angular/common";
import { TooltipModule } from "primeng/tooltip";
import { SvgCacheService } from "./svg-cache.service";
import { take } from "rxjs";

/**
 * SvgIconComponent
 *
 * Provides an easy way to apply size and color over an SVG icon with the help of classes.
 *
 * @description
 * This component allows you to use SVG icons with proper styling control through CSS classes,
 * which is not possible with standard `<img>` elements. It caches SVGs for performance and
 * sanitizes them for security.
 *
 * @replaces Direct usage of `<img>` elements for icons where CSS styling cannot be applied
 *
 * @example
 * ```html
 * <db-svg-icon
 *   [path]="'/assets/icons/catering.svg'"
 *   [class]="'db-icon-lg db-icon-primary'">
 * </db-svg-icon>
 * ```
 *
 * @antipattern
 * ```html
 * <!-- DON'T DO THIS -->
 * <img class="line-item-icon" height="24" src="/assets/icons/catering.svg" alt="catering" />
 * ```
 *
 * @note Ensures consistent styling rather than using img element where CSS cannot be applied.
 */
@Component({
  selector: "db-svg-icon",
  imports: [NgClass, TooltipModule],
  template: `<i [ngClass]="classes()" [innerHTML]="sanitizedSvg"></i>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SvgIconComponent {
  path = input.required<string>();
  class = input<string | string[]>("db-icon-primary");

  protected classes = computed<string>(() => {
    const classValue = this.class();
    const classArray = Array.isArray(classValue) ? classValue : classValue?.split(" ") || [];
    return [...classArray, "flex"].join(" ");
  });

  protected sanitizedSvg: SafeHtml | null = null;

  private svgCacheService = inject(SvgCacheService);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const path = this.path();
      if (path) {
        this.loadSvg(path);
      }
    });
  }

  private loadSvg(path: string) {
    this.svgCacheService
      .getSvg(path)
      .pipe(take(1))
      .subscribe({
        next: (sanitizedSvg) => {
          this.sanitizedSvg = sanitizedSvg;
          this.cdr.markForCheck();
        },
        error: (error: Error) => {
          console.warn(`Error loading SVG from ${path}: `, error);
        },
      });
  }
}
